import { isArray, isString } from '@vue/shared'
import {
  // NOOP,
  buildProps,
  definePropType,
  iconPropType,
  isNumber,
  isUndefined,
  isBoolean,
} from '@element-plus/utils'
import { CHANGE_EVENT, UPDATE_MODEL_EVENT } from '@element-plus/constants'
import { useSizeProp } from '@element-plus/hooks'
import { useTooltipContentProps } from '@element-plus/components/tooltip'
import { tagProps } from '@element-plus/components/tag'
import type { Placement } from '@element-plus/components/popper'
import type { ExtractPropTypes, PropType } from 'vue'
import type MultipleInput from './multiple-input.vue'

export type SuggestionData = Record<string, any>[]
export type MultipleInputSuggestionData = string[] | SuggestionData
export type MultipleInputFetchSuggestions = (
  query: string
) => Promise<MultipleInputSuggestionData> | MultipleInputSuggestionData

export const multipleInputProps = buildProps({
  /**
   * @description value
   */
  modelValue: {
    type: [Array as PropType<string[]>, String],
    required: true,
  },
  /**
   * @description separator for text value
   */
  separator: {
    type: String,
    default: ';',
  },
  /**
   * clearable
   */
  clearable: {
    type: [Boolean, String],
    validator: (val) => {
      return isBoolean(val) || ['Both', 'Tag', 'Input', ''].includes(val)
    },
    default: false,
  },
  /**
   * @description input box size
   */
  size: useSizeProp,
  /**
   * @description input unique
   */
  unique: {
    type: Boolean,
    default: false,
  },
  /**
   * @description whether to disable
   */
  disabled: Boolean,
  /**
   * @description native input readonly
   */
  readonly: {
    type: Boolean,
    default: false,
  },
  /**
   * @description hide innner input when disabled or readonly
   */
  hiddenOnDisabled: {
    type: Boolean,
    default: true,
  },
  /**
   * @description placeholder
   */
  placeholder: {
    type: String,
  },
  /**
   * @description suffix icon
   */
  suffixIcon: {
    type: iconPropType,
  },
  /**
   * @description prefix icon
   */
  prefixIcon: {
    type: iconPropType,
  },
  /**
   * @description native input autocomplete
   */
  autocomplete: {
    type: String,
    default: 'off',
  },
  /**
   * @description native input autofocus
   */
  autofocus: {
    type: Boolean,
    default: false,
  },
  /**
   * @description format content
   */
  formatter: {
    type: Function,
  },
  /**
   * @description parse content
   */
  parser: {
    type: Function,
  },
  /**
   * @description native input id
   */
  id: {
    type: String,
    default: undefined,
  },
  /**
   * @description native input form
   */
  form: {
    type: String,
  },
  /**
   * @description native input aria-label
   */
  label: {
    type: String,
    default: undefined,
  },
  /**
   * @description input tabindex
   */
  tabindex: {
    type: [String, Number],
    default: 0,
  },
  /**
   * @description whether to trigger form validation
   */
  validateEvent: {
    type: Boolean,
    default: true,
  },
  /**
   * @description the max length, no limit when set to 0
   */
  limit: {
    type: Number,
    default: 0,
    validator: (val: number): boolean => val >= 0,
  },
  /**
   * @description whether show limit
   */
  showLimit: {
    type: Boolean,
    default: false,
  },
  /**
   * @description whether to collapse tags to a text when multiple input
   */
  collapseTags: Boolean,
  /**
   * @description The max tags number to be shown. To use this, collapse-tags must be true
   */
  maxCollapseTags: {
    type: Number,
    default: 1,
  },
  /**
   * @description whether show all selected tags when mouse hover text of collapse-tags. To use this, collapse-tags must be true
   */
  collapseTagsTooltip: Boolean,
  /**
   * @description tag type
   */
  tagType: { ...tagProps.type, default: 'info' },
  /**
   * @description tag hit
   */
  tagHit: { type: Boolean, default: false },
  /**
   * @description Tooltip theme, built-in theme: dark / light
   */
  effect: {
    type: String as PropType<'light' | 'dark' | string>,
    default: 'light',
  },
  /**
   * @description prepend and append stretch height
   */
  stretch: Boolean,
  /**
   * @description whether reserve select/button style
   */
  reserveStyle: Boolean,
  /**
   * @description support some recommended tips based on the current input.
   */
  suggestions: {
    type: definePropType<MultipleInputSuggestionData>(Array),
    default: [],
  },
  /**
   * @description a method to fetch input suggestions. When suggestions are ready, invoke `callback(data:[])` to return them to Autocomplete
   */
  fetchSuggestions: {
    type: definePropType<MultipleInputFetchSuggestions>(Function),
  },
  /**
   * @description key name of the input suggestion object for display.
   */
  valueKey: {
    type: String,
    default: 'value',
  },
  /**
   * @description debounce delay when typing, in milliseconds
   */
  debounce: {
    type: Number,
    default: 300,
  },
  /**
   * @description the placeholder of Autocomplete
   */
  placement: {
    type: definePropType<Placement>(String),
    values: [
      'top',
      'top-start',
      'top-end',
      'bottom',
      'bottom-start',
      'bottom-end',
    ],
    default: 'bottom-start',
  },
  /**
   * @description custom class name for autocomplete's dropdown
   */
  popperClass: {
    type: String,
    default: '',
  },
  /**
   * @description whether show suggestions when input focus
   */
  triggerOnFocus: {
    type: Boolean,
    default: true,
  },
  /**
   * @description whether to hide the loading icon in promise search
   */
  hideLoading: {
    type: Boolean,
    default: false,
  },
  teleported: useTooltipContentProps.teleported,
  /**
   * @description whether to highlight first item in remote search suggestions by default
   */
  highlightFirstItem: {
    type: Boolean,
    default: false,
  },
  /**
   * @description whether the width of the dropdown is the same as the input
   */
  fitInputWidth: {
    type: Boolean,
    default: false,
  },
  /**
   * @description whether to blur when select a item of suggestions
   */
  blurOnSelected: {
    type: Boolean,
    default: true,
  },
  /**
   * @description whether to show suggestions even if without match data
   */
  displayWithoutMatch: {
    type: Boolean,
    default: false,
  },
  /**
   * @description custom empty text for suggestions without match data
   */
  noMatchText: {
    type: String,
    default: '',
  },
  /**
   * @description custom suggestion item cannot be selected【Not recommended】
   */
  suggestionItemDisabled: Function,
} as const)
export type MultipleInputProps = ExtractPropTypes<typeof multipleInputProps>

export const multipleInputEmits = {
  [UPDATE_MODEL_EVENT]: (value: string | string[]) =>
    isArray(value) || isString(value),
  [CHANGE_EVENT]: (value: string | string[]) =>
    isArray(value) || isString(value),
  select: (value: any) => !!value,
  input: (value: string) => isString(value),
  focus: (evt: FocusEvent) => evt instanceof FocusEvent,
  blur: (evt: FocusEvent) => evt instanceof FocusEvent,
  clear: () => true,
  'remove-tag': (tag: string, index?: number) =>
    isString(tag) && (isUndefined(index) || isNumber(index)),
  mouseleave: (evt: MouseEvent) => evt instanceof MouseEvent,
  mouseenter: (evt: MouseEvent) => evt instanceof MouseEvent,
  keydown: (evt: KeyboardEvent | Event) => evt instanceof Event,
  compositionstart: (evt: CompositionEvent) => evt instanceof CompositionEvent,
  compositionupdate: (evt: CompositionEvent) => evt instanceof CompositionEvent,
  compositionend: (evt: CompositionEvent) => evt instanceof CompositionEvent,
}
export type MultipleInputEmits = typeof multipleInputEmits

export type MultipleInputInstance = InstanceType<typeof MultipleInput>
